import { FC, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUncontrolledInput } from 'modules/core/hooks';

import { ApplyCpoWrapper, Input, Label } from './styles';

interface IApplyCpoCheckbox {
  name: string;
  disabled?: boolean;
  changeCallback?: VoidFunction;
}

/**
 * "Apply CPOV" on Lender Vehicle Search (SWA-7938). Rendered through the VehicleFilters
 * `filterControls` slot, and only for lenders configured with the Ally CPO guidebook — the
 * caller decides that, this component just binds the form field.
 */
export const ApplyCpoCheckbox: FC<IApplyCpoCheckbox> = ({ name, disabled = false, changeCallback }) => {
  const { t } = useTranslation();
  const { controlField, setValue, fieldValue } = useUncontrolledInput(name);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(name, event.target.checked);
      changeCallback?.();
    },
    [changeCallback, name, setValue],
  );

  const renderCheckbox = useCallback(
    () => (
      <ApplyCpoWrapper data-testid="apply-cpo" $disabled={disabled}>
        <Input
          className="form-check-input position-static m-0"
          id={name}
          type="checkbox"
          checked={fieldValue || false}
          onChange={handleChange}
          disabled={disabled}
        />
        <Label className="form-check-label ml-1" htmlFor={name} disabled={disabled}>
          {t('components.vehicleFilters.applyCpo')}
        </Label>
      </ApplyCpoWrapper>
    ),
    [disabled, fieldValue, handleChange, name, t],
  );

  return <Controller name={name} control={controlField} render={renderCheckbox} />;
};
